/**
 * Connects game signals to SFX playback. Audio is a passive observer here —
 * it only subscribes to Rive events, so the game orchestrator stays unaware of
 * sound.
 *
 * Map: Rive event name → SFX clip base name (file in public/assets/carrace_sfx/).
 *
 *   Boost      → Boost          a racer's boost burst
 *   StartSpin  → StartSpin      the race spin kicks off
 *   CorrectClip→ CorrectClip    correct-result sting
 *   Winning1   → Winning1       winner reveal, variant 1
 *   Winning2   → Winning2       winner reveal, variant 2
 *   Winning3   → Winning3       winner reveal, variant 3
 *   HilightBox → HighlightBox   winner highlight box appears (note: the Rive
 *                               event is spelled "HilightBox", the clip is
 *                               "HighlightBox.mp3")
 *   MouseClick → Mouse-ClickClip a generic in-Rive click (clip "Mouse-ClickClip.mp3")
 *
 * The chip-denomination select / bet-placed sound (`ClickBetSelect`) is NOT
 * here: those are driven from the GameState chokepoint in main.ts (a chip
 * change or a rise in the bet total), not from a Rive event. `CHIP_SELECT_SFX`
 * is exported so it gets preloaded with the rest.
 *
 * NOTE — the Car Racing `.riv` has NO `IsSoundOn` input (it was removed); the
 * artboard emits these cue events unconditionally, so there is no Rive boolean
 * to hold true. Audibility is gated entirely by SoundManager (the on/off
 * toggle). Every Rive event is also logged as `[Rive][event:passthrough]`
 * (riveLayer.ts); if a sound never fires, check the console for the real event
 * name.
 */
import type { RiveLayer } from '../rive/riveLayer';
import type { SoundManager } from './soundManager';

/** Rive event name → SFX clip base name. */
const EVENT_SFX: Readonly<Record<string, string>> = {
  Boost: 'Boost',
  StartSpin: 'StartSpin',
  CorrectClip: 'CorrectClip',
  Winning1: 'Winning1',
  Winning2: 'Winning2',
  Winning3: 'Winning3',
  // Event name is misspelled "HilightBox" in the .riv; the clip is HighlightBox.mp3.
  HilightBox: 'HighlightBox',
  // Generic click cue; the clip is Mouse-ClickClip.mp3.
  MouseClick: 'Mouse-ClickClip',
};

/** Clip played when the selected chip changes or a bet is placed (from GameState). */
export const CHIP_SELECT_SFX = 'ClickBetSelect';

/** Distinct clips referenced anywhere — feed to SoundManager.preload(). */
export const SFX_CLIPS: readonly string[] = Array.from(
  new Set([...Object.values(EVENT_SFX), CHIP_SELECT_SFX]),
);

/** Subscribe SFX playback to the relevant Rive events. */
export function bindGameSounds(rive: RiveLayer, sound: SoundManager): void {
  for (const [event, clip] of Object.entries(EVENT_SFX)) {
    rive.on(event, () => sound.play(clip));
  }
}
